// The three-row settings block at the foot of an album (W39.53).
//
// Extracted from `routes/AlbumDetail.tsx`, which crossed the project's own
// 15 KB route ceiling (`AGENTS.md §3 #10`, enforced by
// `__tests__/route-size-ceiling.test.ts`) once 「下載全部」 and the load-more
// control were wired up.
//
// Two of the three rows were dead before this extraction, and the shape of the
// defect is the point: 「選擇封面照片」 had no `onClick` at all and 「下載全部」
// had none either, yet both rendered with the same ink, the same
// `hover:bg-black/5`, the same 44px row height and the same `›` chevron as the
// one row that worked. On a 393×852 render, tapping either left the DOM
// byte-identical — 21384 → 21384 characters. 「移到垃圾桶」 in the list screen's
// sheet and 「設為封面」 in the photo sheet were the same defect twice more: a
// body that only called `console.warn`.
//
// The classes are written out on each row rather than shared through a
// constant: `__tests__/a11y-control-size.guard.test.ts` reads the literal
// `className` attribute, so a `${ROW}` interpolation hides the 44px size token
// from it, and a control the guard cannot measure is a control nobody checked.

import { Download, Edit, Share2 } from "lucide-react";

import { Card } from "@/components/ui/card";

export interface AlbumSettingsRowsProps {
  /** False when the album has no photos yet — nothing to download or cover. */
  hasPhotos: boolean;
  /** W39.54 — lets the row say 自動封面 vs 手動封面 instead of guessing. */
  coverIsManual: boolean;
  downloadState: { done: number; total: number } | null;
  onShare: () => void;
  onChooseCover: () => void;
  onDownloadAll: () => void;
}

export function AlbumSettingsRows({
  hasPhotos,
  coverIsManual,
  downloadState,
  onShare,
  onChooseCover,
  onDownloadAll,
}: AlbumSettingsRowsProps) {
  return (
    // W53.2 D-1 — was `surface-plain rounded-2xl divide-y divide-hairline
    // shadow-card`, i.e. the L2 contract **minus the border**. On a paper-warm
    // ground a white card with no hairline is legible only because of the
    // shadow, so this was the one place on `/albums/:id` whose card read as a
    // different component rather than the same one. `<Card>` supplies fill,
    // hairline, shadow and radius together, which is the whole point of D-1.
    <Card className="divide-y divide-hairline">
      <button
        type="button"
        onClick={onShare}
        data-testid="album-detail-share"
        className="press flex w-full items-center gap-s-3 px-s-4 py-s-3 text-body text-ink-1 hover:bg-black/5 transition-colors min-h-[44px]"
      >
        <Share2 size={20} className="text-you-strong" aria-hidden />
        <span className="flex-1 text-left">分享相簿</span>
        <span className="text-ink-3 text-title-3" aria-hidden>
          ›
        </span>
      </button>
      <button
        type="button"
        data-testid="album-detail-cover"
        // W39.54 — this row was dead on screen for its whole life: no `onClick`,
        // no `disabled:` variant, and the same `›` as the rows beside it. It
        // stayed dead after W39.53 found it because the cover was server-derived
        // with nowhere to record a choice; the contract it needed now exists.
        className="press flex w-full items-center gap-s-3 px-s-4 py-s-3 text-body text-ink-1 hover:bg-black/5 transition-colors min-h-[44px] disabled:opacity-40 disabled:hover:bg-transparent disabled:pointer-events-none"
        disabled={!hasPhotos}
        onClick={onChooseCover}
      >
        <Edit size={20} className="text-gold-ink" aria-hidden />
        <span className="flex-1 text-left">選擇封面照片</span>
        {hasPhotos ? (
          <>
            <span className="text-body-xs text-ink-3" data-testid="album-cover-mode">
              {coverIsManual ? "手動封面" : "自動封面"}
            </span>
            <span className="text-ink-3 text-title-3" aria-hidden>
              ›
            </span>
          </>
        ) : (
          <span className="text-body-xs text-ink-3" data-testid="album-cover-unavailable">
            尚無照片
          </span>
        )}
      </button>
      <button
        type="button"
        data-testid="album-detail-download"
        className="press flex w-full items-center gap-s-3 px-s-4 py-s-3 text-body text-ink-1 hover:bg-black/5 transition-colors min-h-[44px] disabled:opacity-40 disabled:pointer-events-none"
        disabled={!hasPhotos || downloadState !== null}
        onClick={onDownloadAll}
      >
        <Download size={20} className="text-partner-strong" aria-hidden />
        <span className="flex-1 text-left">
          {downloadState
            ? `正在下載 ${downloadState.done} / ${downloadState.total}…`
            : "下載全部"}
        </span>
        <span className="text-ink-3 text-title-3" aria-hidden>
          ›
        </span>
      </button>
    </Card>
  );
}
